import * as React from "react";
import { Textarea, type TextareaProps } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const TEXTAREA_MIN_HEIGHT_PX = 80;
const TEXTAREA_MAX_HEIGHT_PX = 320;

export const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onInput, value, ...props }, forwardedRef) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    const resizeToFitContent = React.useCallback(() => {
      const element = textareaRef.current;
      if (!element) return;

      element.style.height = "auto";
      const scrollHeight = element.scrollHeight;
      const nextHeight = Math.max(
        TEXTAREA_MIN_HEIGHT_PX,
        Math.min(scrollHeight, TEXTAREA_MAX_HEIGHT_PX),
      );

      element.style.height = `${nextHeight}px`;
      element.style.overflowY =
        scrollHeight > TEXTAREA_MAX_HEIGHT_PX ? "auto" : "hidden";
    }, []);

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;

        if (!forwardedRef) return;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
          return;
        }
        forwardedRef.current = node;
      },
      [forwardedRef],
    );

    React.useEffect(() => {
      resizeToFitContent();
    }, [resizeToFitContent, value]);

    return (
      <Textarea
        {...props}
        value={value}
        ref={setRefs}
        onInput={(event) => {
          resizeToFitContent();
          onInput?.(event);
        }}
        className={cn(
          "min-h-[80px] max-h-[320px] resize-none overflow-hidden",
          className,
        )}
      />
    );
  },
);

AutoGrowTextarea.displayName = "AutoGrowTextarea";
